import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { PanelLeft } from 'lucide-react';
import { cn } from '../../lib/cn';
import { IconButton } from '../ui/button';
import { Sheet } from '../ui/sheet';
import { BottomNav, MehrListe } from './bottom-nav';
import { Sidebar, type NavItem, type SidebarProps } from './sidebar';
import { CountBadge } from '../ui/count-badge';

interface ShellZustand {
  /** Desktop: Sidebar ein- und ausblenden. Mobil: Schublade öffnen, mit `mobilNav` „Mehr“. */
  toggleSidebar: () => void;
  sidebarHidden: boolean;
  /** Unter 768 px keine Schublade: Tab-Leiste oder, mit `mobilNav={false}`, gar keine Navigation. */
  mobilNav: boolean;
  /** Summe der Zahlen im Sidebar-Fuß (ungelesene Benachrichtigungen). */
  hinweise: number;
}

const ShellContext = createContext<ShellZustand | null>(null);

export function useAppShell() {
  const ctx = useContext(ShellContext);
  if (!ctx) throw new Error('useAppShell nur innerhalb von <AppShell> verwenden');
  return ctx;
}

export interface AppShellProps {
  sidebar: SidebarProps;
  /**
   * Tab-Leiste unten unter 768 px statt der Schublade (README „Mobil“, M1).
   * Ohne Angabe bleibt die Schublade. `false`: unter 768 px gar keine
   * Navigation, weder Leiste noch Schublade — für Konto, das aus den anderen
   * Apps aufgerufen wird und oben seinen Rückweg zeigt (`PageHeader zurueck`
   * mit `beschriftet`); die Bereiche wählt die App dann im Seitenkopf.
   */
  mobilNav?:
    | false
    | {
      /** IDs aus `sidebar.sections`, von links nach rechts; der erste ist der Start-Tab. */
      tabs: string[];
      /** Runde Hauptaktion „+“ in der Mitte der Leiste; öffnet Schnell erfassen. */
      schnellErfassen?: { label: string; onClick: () => void };
      /**
       * Ziele nur in „Mehr“, z. B. Profil, Benachrichtigungen und Abmelden, wenn
       * sie am Rechner im Seitenkopf statt im Sidebar-Fuß sitzen. Laufen über
       * dieselben `linkAs` und `onSelect` wie die Sidebar.
       */
      mehrItems?: NavItem[];
    };
  /** Meist ein <PageHeader> und der Inhalt der Seite. */
  children: ReactNode;
  className?: string;
}

/**
 * Aufbau jeder internen App: App-Fenster auf background, links die schwebende
 * Sidebar (8 px Abstand zum Rand), rechts der Inhalt mit space-6 Rand.
 * Unter 768 px wird die Sidebar zur Schublade, mit `mobilNav` zur Tab-Leiste
 * unten und „Mehr“, mit `mobilNav={false}` verschwindet sie ganz.
 */
export function AppShell({ sidebar, mobilNav, children, className }: AppShellProps) {
  const [versteckt, setVersteckt] = useState(false);
  const [mobilOffen, setMobilOffen] = useState(false);
  const [mehrOffen, setMehrOffen] = useState(false);
  const leiste = mobilNav || undefined;
  const mitLeiste = leiste != null;
  // Mit Tab-Leiste oder ganz ohne Navigation (`false`) gibt es keine Schublade.
  const ohneSchublade = mobilNav != null;

  const toggleSidebar = useCallback(() => {
    if (window.matchMedia('(min-width: 768px)').matches) setVersteckt((v) => !v);
    else if (mitLeiste) setMehrOffen((o) => !o);
    else if (!ohneSchublade) setMobilOffen((o) => !o);
  }, [mitLeiste, ohneSchublade]);
  // Was im Fuß zählt, sind Hinweise (ungelesene Benachrichtigungen), keine Mengen wie „Leads 18“.
  const hinweise = (sidebar.footer ?? []).reduce((summe, it) => summe + (it.count ?? 0), 0);
  const ctx = useMemo(
    () => ({ toggleSidebar, sidebarHidden: versteckt, mobilNav: ohneSchublade, hinweise }),
    [toggleSidebar, versteckt, ohneSchublade, hinweise],
  );

  // Eine Quelle der Wahrheit: Tabs und „Mehr“ kommen aus der Sidebar.
  const tabIds = leiste?.tabs ?? [];
  const alleZiele = sidebar.sections.flatMap((s) => s.items);
  const tabs = tabIds.map((id) => alleZiele.find((it) => it.id === id)).filter((it): it is NavItem => it != null);
  const mehrSections = sidebar.sections.map((s) => ({ ...s, items: s.items.filter((it) => !tabIds.includes(it.id)) })).filter((s) => s.items.length > 0);
  const mehrFuss = [...(sidebar.footer ?? []), ...(leiste?.mehrItems ?? [])];
  const mehrCount = mehrFuss.reduce((summe, it) => summe + (it.count ?? 0), 0);
  const mehrAktiv = mehrOffen || (sidebar.activeId != null && !tabIds.includes(sidebar.activeId));
  const waehlen = (id: string) => {
    sidebar.onSelect?.(id);
    setMehrOffen(false);
  };

  return (
    <ShellContext.Provider value={ctx}>
      {/* Oben: Abstand der Statusleiste. In der installierten App legt iOS unter
          die Statusleiste einen Weichzeichner, der gut ein Drittel tiefer reicht
          als die Leiste selbst (gemessen am iPhone mit Dynamic Island, 23.10.2026:
          Leiste 59 pt, unscharf bis etwa 90 pt). Der Seitenkopf fängt deshalb dort
          erst beim 1,6-Fachen an. Ohne Statusleiste (Rechner, Browser) ist der Wert
          0 und alles bleibt wie bisher. */}
      <div
        className={cn(
          'flex min-h-dvh bg-background pt-[env(safe-area-inset-top)] text-foreground standalone:pt-[calc(env(safe-area-inset-top)*1.6)]',
          className,
        )}
      >
        {!versteckt && (
          <div className="sticky top-0 hidden h-dvh shrink-0 py-2 pr-2 pl-[max(0.5rem,env(safe-area-inset-left))] md:block">
            <Sidebar {...sidebar} />
          </div>
        )}
        {!ohneSchublade && (
          <DialogPrimitive.Root open={mobilOffen} onOpenChange={setMobilOffen}>
            <DialogPrimitive.Portal>
              <DialogPrimitive.Overlay className="fixed inset-0 z-[2000] bg-background/70 animate-ek-in md:hidden" />
              <DialogPrimitive.Content
                className="fixed inset-y-0 left-0 z-[2000] p-2 pl-[max(0.5rem,env(safe-area-inset-left))] animate-ek-in md:hidden"
                aria-describedby={undefined}
              >
                <DialogPrimitive.Title className="sr-only">Navigation</DialogPrimitive.Title>
                <Sidebar
                  {...sidebar}
                  onSelect={(id) => {
                    sidebar.onSelect?.(id);
                    setMobilOffen(false);
                  }}
                />
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          </DialogPrimitive.Root>
        )}
        <div
          className={cn(
            'flex min-w-0 flex-1 flex-col pl-[max(1rem,env(safe-area-inset-left))] pr-[max(1rem,env(safe-area-inset-right))] md:pl-6 md:pr-[max(1.5rem,env(safe-area-inset-right))]',
            // Unten Platz für die Tab-Leiste (60 px) und die Home-Leiste.
            mitLeiste ? 'pb-[calc(3.75rem+env(safe-area-inset-bottom)+1.5rem)] md:pb-6' : 'pb-[max(1.5rem,env(safe-area-inset-bottom))]',
          )}
        >
          {children}
        </div>
        {leiste && (
          <>
            <BottomNav
              tabs={tabs}
              activeId={sidebar.activeId}
              mehrAktiv={mehrAktiv}
              onMehr={() => setMehrOffen(true)}
              mehrCount={mehrCount}
              onSelect={waehlen}
              linkAs={sidebar.linkAs ?? 'a'}
              schnellErfassen={leiste.schnellErfassen}
            />
            <Sheet open={mehrOffen} onOpenChange={setMehrOffen} title="Mehr">
              <MehrListe sections={mehrSections} footer={mehrFuss} activeId={sidebar.activeId} onSelect={waehlen} linkAs={sidebar.linkAs ?? 'a'} />
            </Sheet>
          </>
        )}
      </div>
    </ShellContext.Provider>
  );
}

/** IconButton zum Ein- und Ausblenden der Sidebar, für PageHeader `leading`. Mit Tab-Leiste unter 768 px ausgeblendet. */
export function SidebarToggle() {
  const { toggleSidebar, sidebarHidden, mobilNav, hinweise } = useAppShell();
  // Ist die Sidebar eingeklappt, sieht man ihren Fuß nicht: Die Zahl der
  // ungelesenen Benachrichtigungen wandert an den Knopf, der sie einblendet.
  const zahl = sidebarHidden && hinweise > 0 ? hinweise : 0;
  return (
    <span className={cn('relative inline-flex', mobilNav && 'max-md:hidden')}>
      <IconButton
        icon={PanelLeft}
        label={zahl > 0 ? `Seitenleiste einblenden, ${zahl} ungelesene Benachrichtigungen` : sidebarHidden ? 'Seitenleiste einblenden' : 'Seitenleiste ausblenden'}
        variant="ghost"
        onClick={toggleSidebar}
      />
      {zahl > 0 && (
        <CountBadge tone="primary" className="pointer-events-none absolute -right-1 -top-1">
          {zahl}
        </CountBadge>
      )}
    </span>
  );
}
